import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  booleanField,
  conversionRatioField8dp,
  sharesField,
  simpleMultilineTextField,
  simpleTextField,
} from '../../../data-fields/CommonFields';
import { getValuationImpactField, securityField } from '../../../data-fields/TransactionFields';
import { RendererType } from '../../../data-models/field-definition';
import { SellBy } from '../../../view-models/transaction-form.view-model';
import { ICompanySearchMeta, ISelectMeta } from '../../../data-models/field3.data-model';
import { ExitType, ITransactionDataModel, ProceedsTypes } from '../../../data-models/transaction.data-model';
import { useCreateCompanyAndUpdateState } from '../../../services/hooks/useCreateCompanyAndUpdateState';
import { SearchType } from '../../../services/queries/MaggieCompanyQueries';
import { companyDataModelToSearchResultDataModel } from '../../../util/companyUtils';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { createFormField } from '../../../view-models/form.view-model';
import { useSharesSoldField } from '../../Finance/components/TransactionModal/Forms/schemas/Equity/EquitySellSchema';
import { useGetAmountField } from './TransactionFormUtils';
import { useFundDateCurrencyFields } from './useFundDateCurrencyFields';

export function useEquityFields(companyId: number) {
  const { showStockSplitInEquitySell } = useFlags<MaggieFeatureFlags>();
  const getAmountField = useGetAmountField<ITransactionDataModel>('currencyid');
  const getCommonInitialFields = useFundDateCurrencyFields();
  const getAcquirerField = useAcquirerField();
  const getSplitAcquirerField = useSplitAcquirerField();
  const getCashFields = useCashFields();
  const getStockProceedsFields = useStockProceedsFields();
  const getValuationImpactFields = useValuationImpactFields();
  const getSharesBySecurityFields = useSharesSoldField(companyId);
  const sellBy = useWatch({ name: '_viewModel.sellBy' }) as SellBy | undefined;

  const getEquityBuyFields = useCallback(() => {
    return [
      ...getCommonInitialFields(),
      securityField(),
      getAmountField({ key: 'investmentAmount', label: 'InvestmentAmount', required: true }),
      sharesField({ key: 'noOfShares', label: 'Shares Purchased', required: true }),
      getValuationImpactField(),
      conversionRatioField8dp(),
      simpleMultilineTextField({ key: 'notes', label: 'Notes' }),
    ];
  }, [getAmountField, getCommonInitialFields]);

  const getEquitySellFields = useCallback(() => {
    const res = [
      ...getCommonInitialFields(),
      proceedsTypeField(),
      exitTypeField(),
      ...getAcquirerField(),
      ...getSplitAcquirerField(),
      getAmountField({ key: 'exitValuation', label: 'Exit Valuation' }),
      getValuationImpactField(),
      ...getCashFields(),
      ...getStockProceedsFields(),
      ...getValuationImpactFields(),
      simpleMultilineTextField({ key: 'notes', label: 'Notes' }),
    ];

    const soldFields =
      sellBy === SellBy.returnOfCapitalAmount
        ? [
            getAmountField({
              key: 'returnOfCapitalAmount',
              label: 'Return of Capital Amount',
              required: true,
            }),
          ]
        : showStockSplitInEquitySell
          ? [
              booleanField({
                key: '_viewModel.useSplitByRestructureId',
                label: 'Show shares sold by security',
                required: true,
              }),
              ...getSharesBySecurityFields(),
            ]
          : [sharesField({ key: 'noOfShares', label: 'Shares Sold', required: true })];
    res.splice(4, 0, sellByField(), ...soldFields);

    return res;
  }, [
    getCommonInitialFields,
    getAcquirerField,
    getSplitAcquirerField,
    getAmountField,
    getCashFields,
    getStockProceedsFields,
    getValuationImpactFields,
    showStockSplitInEquitySell,
    getSharesBySecurityFields,
    sellBy,
  ]);

  const getEquitySecondaryPurchaseFields = useCallback(() => {
    return [
      ...getCommonInitialFields(),
      securityField(),
      getAmountField({ key: 'investmentAmount', label: 'InvestmentAmount', required: true }),
      sharesField({ key: 'noOfShares', label: 'New Shares Purchased', required: true }),
      getValuationImpactField({ required: true }),
      ...getValuationImpactFields(),
      conversionRatioField8dp(),
      simpleMultilineTextField({ key: 'notes', label: 'Notes' }),
    ];
  }, [getAmountField, getCommonInitialFields, getValuationImpactFields]);

  return {
    getEquityBuyFields,
    getEquitySellFields,
    getEquitySecondaryPurchaseFields,
  };
}

function useAcquirerField() {
  const { watch, setValue } = useFormContext();
  const createCompany = useCreateCompanyAndUpdateState();

  return useCallback(() => {
    if (!watch('exitType')) {
      return [];
    }
    return [
      createFormField<ICompanySearchMeta>({
        key: 'acquirer',
        label: 'Acquirer',
        renderer: RendererType.companySearch,
        rendererMeta: {
          multiSelect: false,
          // The acquirer can be created inline; picks resolve to a real company id so the
          // received equity can be tracked against it.
          showAdd: true,
          addLabel: (inputText) => `Create new company "${inputText}"`,
          searchType: SearchType.Company,
          createOnSelection: true,
          onAdd: async (name: unknown) => {
            const created = await createCompany({ name: name as string });
            if (created) {
              setValue('acquirer', companyDataModelToSearchResultDataModel(created));
            }
          },
        },
      }),
    ];
  }, [watch, setValue, createCompany]);
}

// On: the received equity is restructured into the acquirer as its own holding
// ("broken out"); off keeps it folded into the existing record ("condensed").
function useSplitAcquirerField() {
  const { watch } = useFormContext();

  return useCallback(() => {
    if (!watch('exitType')) {
      return [];
    }
    return [
      booleanField({
        key: 'splitAcquirerIntoSeparateItem',
        label: 'Track acquirer as a separate company',
      }),
    ];
  }, [watch]);
}

function useCashFields() {
  const { watch } = useFormContext();
  const getAmountField = useGetAmountField<ITransactionDataModel>('currencyid');

  return useCallback(() => {
    if (!watch('proceedsType')?.includes(ProceedsTypes.Cash)) {
      return [];
    }

    return [
      getAmountField({ key: 'receivedAmount', label: 'Cash Received', required: true }),
      getAmountField({ key: 'escrowAmount', label: 'Escrow Amount', required: true }),
    ];
  }, [getAmountField, watch]);
}

function useStockProceedsFields() {
  const { watch } = useFormContext();
  const getAmountField = useGetAmountField<ITransactionDataModel>('currencyid');

  return useCallback(() => {
    if (!watch('proceedsType')?.includes(ProceedsTypes.StockProceeds)) {
      return [];
    }

    return [
      sharesField({ key: 'sharesReceived', label: 'Shares Received from Acquirer', required: true }),
      getAmountField({ key: 'ppsSharesReceived', label: 'PPS of Shares Received', required: true }),
    ];
  }, [getAmountField, watch]);
}

function useValuationImpactFields() {
  const { watch } = useFormContext();

  return useCallback(() => {
    if (!watch('valuationImpact')) {
      return [];
    }

    return [
      simpleTextField({
        key: 'fmvBasis',
        label: 'FMV Basis',
      }),
    ];
  }, [watch]);
}

export function proceedsTypeField() {
  return createFormField<ISelectMeta<string>>({
    key: 'proceedsType',
    label: 'Proceeds Type',
    renderer: RendererType.multiSelect,
    rendererMeta: {
      values: Object.values(ProceedsTypes).map((type) => ({ displayName: type, value: type })),
      multi: true,
    },
    required: true,
  });
}

export function sellByField() {
  return createFormField<ISelectMeta<string>>({
    key: '_viewModel.sellBy',
    label: 'Sell By',
    required: true,
    renderer: RendererType.singleSelect,
    disableClearable: true,
    rendererMeta: {
      multi: false,
      values: Object.values(SellBy).map((type) => ({ displayName: type, value: type })),
    },
  });
}

export function exitTypeField() {
  return createFormField<ISelectMeta<string>>({
    key: 'exitType',
    label: 'Exit Type',
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: Object.values(ExitType).map((type) => ({ displayName: type, value: type })),
    },
  });
}
